import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { productos as productosMock } from '../../mocks/ropa';

interface Producto {
  id: string;
  nombre: string;
  precio: string;
  tallas: string;
  imagen: string;
}

const PRODUCTOS_BASE: Producto[] = productosMock.map(p => ({
  id: String(p.id),
  nombre: p.nombre,
  precio: p.precio,
  tallas: p.tallas,
  imagen: p.imagen,
}));

export default function AdminCatalogoPage() {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [productoEditando, setProductoEditando] = useState<Producto | null>(null);
  const [mensajePublicacion, setMensajePublicacion] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState<string | null>(null);
  const [confirmarEliminar, setConfirmarEliminar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    nombre: '',
    precio: '',
    tallas: '',
    imagen: '',
  });

  useEffect(() => {
    cargarProductos();
  }, []);

  const cargarProductos = async () => {
    setCargando(true);
    setErrorMensaje('');
    try {
      const PAGE_SIZE = 8;
      let allData: any[] = [];
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('productos_ropa')
          .select('*')
          .order('created_at', { ascending: true })
          .range(from, from + PAGE_SIZE - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allData = [...allData, ...data];
          from += PAGE_SIZE;
          hasMore = data.length === PAGE_SIZE;
        } else {
          hasMore = false;
        }
      }

      if (allData.length > 0) {
        const mapped: Producto[] = allData.map((p: any) => ({
          id: p.id,
          nombre: p.nombre,
          precio: String(p.precio),
          tallas: Array.isArray(p.tallas) ? p.tallas.join(', ') : String(p.tallas ?? ''),
          imagen: p.imagen ?? '',
        }));
        setProductos(mapped);
      } else {
        setProductos(PRODUCTOS_BASE);
      }
    } catch (err: any) {
      console.error('Error cargando productos:', err);
      const msg = err?.message || err?.toString() || 'Error desconocido';
      setErrorMensaje(`Error al cargar productos desde la nube: ${msg}`);
      setProductos(PRODUCTOS_BASE);
    } finally {
      setCargando(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImagenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFormData(prev => ({ ...prev, imagen: ev.target?.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const abrirFormularioNuevo = () => {
    setFormData({ nombre: '', precio: '', tallas: '', imagen: '' });
    setProductoEditando(null);
    setMostrarFormulario(true);
  };

  const abrirFormularioEditar = (producto: Producto) => {
    setFormData({
      nombre: producto.nombre,
      precio: producto.precio,
      tallas: producto.tallas,
      imagen: producto.imagen,
    });
    setProductoEditando(producto);
    setMostrarFormulario(true);
  };

  const cerrarFormulario = () => {
    setMostrarFormulario(false);
    setProductoEditando(null);
    setFormData({ nombre: '', precio: '', tallas: '', imagen: '' });
  };

  const precioNumerico = (precio: string): number => {
    const limpio = precio.replace(/[^0-9.,]/g, '').replace(',', '.');
    return parseFloat(limpio) || 0;
  };

  const tallasArray = (tallas: string): string[] =>
    tallas.split(',').map(t => t.trim()).filter(Boolean);

  const guardarProducto = async () => {
    if (!formData.nombre || !formData.precio || !formData.tallas || !formData.imagen) {
      setErrorMensaje('Por favor completa todos los campos obligatorios.');
      setTimeout(() => setErrorMensaje(''), 3000);
      return;
    }

    setGuardando(true);
    try {
      if (productoEditando) {
        const esUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productoEditando.id);

        if (esUUID) {
          const { error } = await supabase
            .from('productos_ropa')
            .update({
              nombre: formData.nombre,
              precio: precioNumerico(formData.precio),
              tallas: tallasArray(formData.tallas),
              imagen: formData.imagen,
            })
            .eq('id', productoEditando.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('productos_ropa')
            .insert({
              nombre: formData.nombre,
              precio: precioNumerico(formData.precio),
              tallas: tallasArray(formData.tallas),
              imagen: formData.imagen,
            });
          if (error) throw error;
        }
      } else {
        const { error } = await supabase
          .from('productos_ropa')
          .insert({
            nombre: formData.nombre,
            precio: precioNumerico(formData.precio),
            tallas: tallasArray(formData.tallas),
            imagen: formData.imagen,
          });
        if (error) throw error;
      }

      await cargarProductos();
      cerrarFormulario();
    } catch (err) {
      console.error('Error guardando producto:', err);
      setErrorMensaje('Ocurrió un error al guardar el producto. Intenta de nuevo.');
      setTimeout(() => setErrorMensaje(''), 4000);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarProducto = async (id: string) => {
    setEliminando(id);
    try {
      const esUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      if (esUUID) {
        const { error } = await supabase
          .from('productos_ropa')
          .delete()
          .eq('id', id);
        if (error) throw error;
      }
      setProductos(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error eliminando producto:', err);
      setErrorMensaje('No se pudo eliminar el producto. Intenta de nuevo.');
      setTimeout(() => setErrorMensaje(''), 4000);
    } finally {
      setEliminando(null);
      setConfirmarEliminar(null);
    }
  };

  const publicarCambios = async () => {
    setGuardando(true);
    try {
      const { data: existentes, error: fetchError } = await supabase
        .from('productos_ropa')
        .select('id');
      if (fetchError) throw fetchError;

      const idsExistentes = new Set((existentes ?? []).map((p: any) => p.id));
      const productosSinUUID = productos.filter(p =>
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.id)
        && !idsExistentes.has(p.id)
      );

      if (productosSinUUID.length > 0) {
        const inserts = productosSinUUID.map(p => ({
          nombre: p.nombre,
          precio: precioNumerico(p.precio),
          tallas: tallasArray(p.tallas),
          imagen: p.imagen,
        }));
        const { error: insertError } = await supabase
          .from('productos_ropa')
          .insert(inserts);
        if (insertError) throw insertError;
      }

      await cargarProductos();
      setMensajePublicacion(true);
      setTimeout(() => setMensajePublicacion(false), 4000);
    } catch (err) {
      console.error('Error publicando cambios:', err);
      setErrorMensaje('Error al publicar los cambios. Intenta de nuevo.');
      setTimeout(() => setErrorMensaje(''), 4000);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-2xl p-8 mb-8">
          <h1 className="text-4xl font-bold text-stone-800 mb-4 font-serif">
            Administrador de catálogo de ropa
          </h1>
          <p className="text-stone-600 text-lg leading-relaxed">
            Desde esta página puedes agregar, editar o eliminar productos del catálogo de ropa.
            Los cambios se guardan en la nube y son visibles para todos los visitantes.
          </p>
        </div>

        {/* Botones principales */}
        <div className="flex flex-wrap gap-4 mb-6">
          <button
            onClick={abrirFormularioNuevo}
            className="bg-stone-800 hover:bg-stone-900 text-white px-8 py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-2 text-xl"></i>
            Agregar producto
          </button>

          <button
            onClick={publicarCambios}
            disabled={guardando}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {guardando ? (
              <><i className="ri-loader-4-line mr-2 text-xl animate-spin"></i>Publicando...</>
            ) : (
              <><i className="ri-upload-cloud-line mr-2 text-xl"></i>Publicar cambios</>
            )}
          </button>
        </div>

        {/* Mensaje de éxito */}
        {mensajePublicacion && (
          <div className="bg-green-50 border-2 border-green-500 text-green-800 px-6 py-4 rounded-xl mb-6 flex items-center gap-3">
            <i className="ri-checkbox-circle-line text-2xl"></i>
            <div>
              <p className="font-bold text-lg">¡Cambios publicados en la nube!</p>
              <p className="text-sm text-green-700">Todos los visitantes verán los productos actualizados.</p>
            </div>
          </div>
        )}

        {/* Mensaje de error */}
        {errorMensaje && (
          <div className="bg-red-50 border-2 border-red-400 text-red-800 px-6 py-4 rounded-xl mb-6 flex items-center gap-3">
            <i className="ri-error-warning-line text-2xl"></i>
            <span className="font-semibold">{errorMensaje}</span>
          </div>
        )}

        {/* Modal formulario */}
        {mostrarFormulario && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <h2 className="text-3xl font-bold text-stone-800 mb-6 font-serif">
                  {productoEditando ? 'Editar producto' : 'Agregar nuevo producto'}
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-stone-700 font-semibold mb-2">
                      Imagen del producto *
                    </label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-stone-300 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-stone-500 hover:bg-stone-50 transition-all"
                    >
                      {formData.imagen ? (
                        <img
                          src={formData.imagen}
                          alt="Vista previa"
                          className="w-full h-64 object-cover rounded-xl"
                        />
                      ) : (
                        <>
                          <i className="ri-image-add-line text-5xl text-stone-400 mb-3"></i>
                          <p className="text-stone-500 font-medium">Haz clic para seleccionar una imagen</p>
                          <p className="text-stone-400 text-sm mt-1">JPG, PNG, WEBP</p>
                        </>
                      )}
                    </div>
                    {formData.imagen && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-3 text-sm text-stone-600 underline cursor-pointer"
                      >
                        Cambiar imagen
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImagenChange}
                      className="hidden"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-700 font-semibold mb-2">
                      Título del producto *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      placeholder="Ej: Chaqueta de Cuero"
                      className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-stone-800 focus:outline-none text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-700 font-semibold mb-2">
                      Precio *
                    </label>
                    <input
                      type="text"
                      name="precio"
                      value={formData.precio}
                      onChange={handleInputChange}
                      placeholder="Ej: $25.000"
                      className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-stone-800 focus:outline-none text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-700 font-semibold mb-2">
                      Talla(s) *
                    </label>
                    <input
                      type="text"
                      name="tallas"
                      value={formData.tallas}
                      onChange={handleInputChange}
                      placeholder="Ej: S, M, L o XL"
                      className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-stone-800 focus:outline-none text-sm"
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={guardarProducto}
                    disabled={guardando}
                    className="flex-1 bg-stone-800 hover:bg-stone-900 text-white px-6 py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {guardando ? (
                      <><i className="ri-loader-4-line mr-2 animate-spin"></i>Guardando...</>
                    ) : (
                      productoEditando ? 'Guardar cambios' : 'Guardar producto'
                    )}
                  </button>
                  <button
                    onClick={cerrarFormulario}
                    disabled={guardando}
                    className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-800 px-6 py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal confirmar eliminación */}
        {confirmarEliminar && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                <i className="ri-delete-bin-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-2">¿Eliminar producto?</h3>
              <p className="text-stone-500 mb-6">Esta acción no se puede deshacer. El producto se eliminará de la nube.</p>
              <div className="flex gap-4">
                <button
                  onClick={() => eliminarProducto(confirmarEliminar)}
                  disabled={eliminando === confirmarEliminar}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {eliminando === confirmarEliminar ? (
                    <><i className="ri-loader-4-line mr-2 animate-spin"></i>Eliminando...</>
                  ) : 'Sí, eliminar'}
                </button>
                <button
                  onClick={() => setConfirmarEliminar(null)}
                  className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-800 px-6 py-3 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Listado de productos */}
        <div className="bg-white rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-stone-800 mb-6">
            Productos del catálogo ({cargando ? '...' : productos.length})
          </h2>

          {cargando ? (
            <div className="text-center py-16">
              <i className="ri-loader-4-line text-5xl text-stone-400 animate-spin mb-4"></i>
              <p className="text-stone-500 text-lg">Cargando productos desde la nube...</p>
            </div>
          ) : productos.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-shopping-bag-line text-6xl text-stone-300 mb-4"></i>
              <p className="text-stone-500 text-lg">No hay productos en el catálogo. Agrega el primero.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {productos.map((producto) => (
                <div
                  key={producto.id}
                  className="border-2 border-stone-200 rounded-xl overflow-hidden hover:border-stone-400 transition-all"
                >
                  <div className="h-64 w-full overflow-hidden bg-stone-50">
                    <img
                      src={producto.imagen}
                      alt={producto.nombre}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400?text=Sin+imagen';
                      }}
                    />
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <h3 className="font-bold text-stone-900 text-lg mb-2">{producto.nombre}</h3>
                      <p className="text-2xl font-bold text-stone-800 mb-2">{producto.precio}</p>
                      <p className="text-sm text-stone-600">
                        <strong>Talla(s):</strong> {producto.tallas}
                      </p>
                    </div>
                    <div className="flex gap-3 pt-4 border-t border-stone-200">
                      <button
                        onClick={() => abrirFormularioEditar(producto)}
                        className="flex-1 bg-stone-800 hover:bg-stone-900 text-white px-4 py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap text-sm"
                      >
                        <i className="ri-edit-line mr-2"></i>Editar
                      </button>
                      <button
                        onClick={() => setConfirmarEliminar(producto.id)}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap text-sm"
                      >
                        <i className="ri-delete-bin-line mr-2"></i>Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
